import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Input, Space, Switch, Table, Tag, notification } from 'antd';
import notificationSettingsService from '../../services/notificationSettingsService';

const EVENT_FIELDS = [
  { key: 'order_created', label: 'Order confirmations', defaultLabel: 'Default Off' },
  { key: 'property_created', label: 'New properties', defaultLabel: 'Default Off' },
  { key: 'linen_overheld', label: 'Linen over 15 days', defaultLabel: 'Default On' },
];

export default function NotificationSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [platform, setPlatform] = useState({ order_created: false, property_created: false, linen_overheld: true, welcome_email_note: '' });
  const [users, setUsers] = useState([]);
  const [clientSearch, setClientSearch] = useState('');

  const filteredUsers = useMemo(() => {
    const query = clientSearch.trim().toLowerCase();
    if (!query) return users;
    return users.filter(user => [user.name, user.company_name, user.email]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(query)));
  }, [clientSearch, users]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationSettingsService.getAll();
      setPlatform(current => ({ ...current, ...(data.platform || {}) }));
      setUsers(data.users || []);
    } catch (error) {
      notification.error({ message: 'Unable to load notification settings', description: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePlatform = async () => {
    try {
      setSaving(true);
      const response = await notificationSettingsService.updatePlatform(platform);
      setPlatform(response.platform || platform);
      notification.success({ message: 'Platform notification settings saved' });
      await load();
    } catch (error) {
      notification.error({ message: 'Unable to save settings', description: error.message });
    } finally {
      setSaving(false);
    }
  };

  const updateClient = useCallback(async (record, field, value) => {
    try {
      const next = { ...(record.override || {}), [field]: value };
      const payload = EVENT_FIELDS.reduce((result, item) => ({ ...result, [item.key]: next[item.key] ?? null }), {});
      const response = await notificationSettingsService.updateUser(record.id, payload);
      setUsers(current => current.map(user => user.id === record.id ? { ...user, override: response.override, effective: response.effective } : user));
      notification.success({ message: `Updated ${record.name || record.email}` });
    } catch (error) {
      notification.error({ message: 'Unable to update client notifications', description: error.message });
    }
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Client',
      key: 'client',
      render: (_, record) => <div><strong>{record.name || record.company_name || record.email}</strong><div className="text-gray-500">{record.email}</div></div>,
    },
    ...EVENT_FIELDS.map(field => ({
      title: field.label,
      key: field.key,
      render: (_, record) => {
        const inherits = record.override?.[field.key] === null || record.override?.[field.key] === undefined;
        const enabled = !!record.effective?.[field.key];
        return (
          <Space direction="vertical" size={2}>
            <Switch checked={enabled} onChange={value => updateClient(record, field.key, value)} checkedChildren="On" unCheckedChildren="Off" />
            <Tag color={inherits ? 'blue' : 'gold'}>{inherits ? 'Platform default' : 'Client override'}</Tag>
            {!inherits ? <Button size="small" type="link" onClick={() => updateClient(record, field.key, null)}>Use platform</Button> : null}
          </Space>
        );
      },
    })),
  ], [updateClient]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-golden-navy mb-4">Notification Settings</h1>
      <Alert className="mb-4" type="info" showIcon message="Platform switches are defaults. Each client can inherit them or have an individual On/Off override." />
      <Card title="Platform defaults" loading={loading} className="mb-6">
        <Space size="large" wrap>
          {EVENT_FIELDS.map(field => (
            <div key={field.key}>
              <div className="font-semibold mb-2">{field.label}</div>
              <Switch checked={!!platform[field.key]} onChange={value => setPlatform(current => ({ ...current, [field.key]: value }))} checkedChildren="On" unCheckedChildren="Off" />
              <Tag className="ml-2">{field.defaultLabel}</Tag>
            </div>
          ))}
        </Space>
        <div className="mt-6">
          <div className="font-semibold mb-2">Welcome email note</div>
          <Input.TextArea rows={5} maxLength={5000} showCount value={platform.welcome_email_note || ''} onChange={event => setPlatform(current => ({ ...current, welcome_email_note: event.target.value }))} placeholder="Editable note included in every welcome/create-password email" />
        </div>
        <Button type="primary" className="bg-golden-navy mt-4" loading={saving} onClick={savePlatform}>Save platform settings</Button>
      </Card>
      <Card title="Individual client settings">
        <Input.Search
          allowClear
          value={clientSearch}
          onChange={event => setClientSearch(event.target.value)}
          placeholder="Search clients by name, company or email"
          className="mb-4"
          style={{ maxWidth: 420 }}
        />
        <Table rowKey="id" loading={loading} dataSource={filteredUsers} columns={columns} pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }} scroll={{ x: 900 }} />
      </Card>
    </div>
  );
}
